import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  AdGeneratorSettings,
  AdGeneratorSettingsPatch,
  FALLBACK_SETTINGS,
  SettingsApiResponse,
} from './types';

export const AD_GENERATOR_SETTINGS_KEY = ['ad-generator-settings'] as const;

/**
 * Ustawienia czyta cały edytor (próbnik kolorów, kroje, limit zdjęć dla AI),
 * więc trzymamy je w cache bezterminowo i odświeżamy tylko po zapisie.
 * Inaczej każde przełączenie narzędzia budziłoby funkcję po to samo.
 */
export const useGetAdGeneratorSettings = (): UseQueryResult<AdGeneratorSettings, AxiosError> => useQuery<
  AdGeneratorSettings,
  AxiosError
>({
  queryKey: AD_GENERATOR_SETTINGS_KEY,
  queryFn: async () => {
    const res = await axiosInstance.get<SettingsApiResponse<AdGeneratorSettings>>('/v3-getAdGeneratorSettings');
    return res.data.data;
  },
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});

/**
 * Ustawienia z domyślnymi w tle. Komponenty edytora nie muszą sprawdzać stanu
 * ładowania: dopóki odpowiedź nie dojdzie, pracują na wartościach startowych.
 */
export const useAdGeneratorSettings = (): AdGeneratorSettings => {
  const { data } = useGetAdGeneratorSettings();
  return data ?? FALLBACK_SETTINGS;
};

export const useUpdateAdGeneratorSettings = (
  options?: UseMutationOptions<AdGeneratorSettings, AxiosError, AdGeneratorSettingsPatch>,
): UseMutationResult<AdGeneratorSettings, AxiosError, AdGeneratorSettingsPatch> => {
  const queryClient = useQueryClient();

  return useMutation<AdGeneratorSettings, AxiosError, AdGeneratorSettingsPatch>({
    mutationFn: async (patch) => {
      const res = await axiosInstance.put<SettingsApiResponse<AdGeneratorSettings>>(
        '/v3-updateAdGeneratorSettings',
        patch,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (result, ...rest) => {
      // Odpowiedź niesie stan po walidacji, więc wstawiamy ją wprost zamiast
      // unieważniać: panel od razu pokazuje to, co faktycznie zapisano.
      queryClient.setQueryData(AD_GENERATOR_SETTINGS_KEY, result);
      options?.onSuccess?.(result, ...rest);
    },
  });
};
